// src/pages/contracts/DirectContractAccess.jsx
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box, Typography, Button, Card, CardContent, CardActions,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField,
  Alert, CircularProgress, Container, Chip, IconButton, Divider, Tooltip
} from '@mui/material';
import {
  CheckCircle as SignedIcon,
  Description as DocumentIcon,
  Error as ErrorIcon,
  Gavel as ContractIcon,
  ArrowBack as BackIcon,
  Refresh as RefreshIcon,
  Close as CloseIcon,
  Download as DownloadIcon
} from '@mui/icons-material';
import PageTitle from '../../Components/PageTitle';
import client from '../../API';

import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

const statusColor = (status) => {
  switch (status) {
    case 'signed':
    case 'agreed':
      return 'success';
    case 'pending':
      return 'warning';
    case 'declined':
      return 'error';
    default:
      return 'default';
  }
};

const DirectContractAccess = () => {
  const { id: assignmentId } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [contractData, setContractData] = useState(null);
  const [error, setError] = useState(null);
  const [showDeclineDialog, setShowDeclineDialog] = useState(false);
  const [declineReason, setDeclineReason] = useState('');
  const [completed, setCompleted] = useState(false);

  const completedDocRef = useRef(null);

  const load = useCallback(async () => {
    if (!assignmentId) {
      setError('Invalid contract access link');
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      setError(null);
      const data = await client.contracts.getContractFromAssignment(assignmentId);
      setContractData(data);
    } catch (e) {
      console.error(e);
      setError(e.message || 'Failed to load contract');
    } finally {
      setLoading(false);
    }
  }, [assignmentId]);

  useEffect(() => { load(); }, [load]);

  const currentSigId = contractData?.signature?.id;

  const handleAgree = async () => {
    if (!contractData?.is_simple) return handleSign();
    try {
      setActionLoading(true);
      setError(null);
      const res = await client.contracts.agreeToContract(currentSigId);
      setCompleted(true);
      setContractData(prev => ({ ...prev, signature: res.signature || res.data?.signature }));
      await load(); // refresh list
    } catch (e) {
      console.error(e);
      setError(e.message);
    } finally {
      setActionLoading(false);
    }
  };

  const handleSign = async () => {
    try {
      setActionLoading(true);
      setError(null);
      const res = await client.contracts.signContract(currentSigId);
      setCompleted(true);
      setContractData(prev => ({ ...prev, signature: res.signature || res.data?.signature }));
      await load(); // refresh list
    } catch (e) {
      console.error(e);
      setError(e.message);
    } finally {
      setActionLoading(false);
    }
  };

  const handleDecline = async () => {
    if (!declineReason.trim()) {
      setError('Please provide a reason for declining');
      return;
    }
    try {
      setActionLoading(true);
      setError(null);
      const res = await client.contracts.declineContract(currentSigId, declineReason);
      setCompleted(true);
      setContractData(prev => ({ ...prev, signature: res.signature || res.data?.signature }));
      setShowDeclineDialog(false);
      await load(); // refresh list
    } catch (e) {
      console.error(e);
      setError(e.message);
    } finally {
      setActionLoading(false);
    }
  };

  const handleClose = () => navigate('/dashboard');

  const downloadPdf = async () => {
    if (!completedDocRef.current) return;
    const element = completedDocRef.current;
    const canvas = await html2canvas(element, { scale: 2, useCORS: true, logging: false });
    const imgData = canvas.toDataURL('image/png');

    const pdf = new jsPDF('p', 'pt', 'a4');
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();

    const imgWidth = pageWidth;
    const imgHeight = (canvas.height * imgWidth) / canvas.width;

    let heightLeft = imgHeight;
    let position = 0;

    pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
    heightLeft -= pageHeight;

    while (heightLeft > 0) {
      position = heightLeft - imgHeight;
      pdf.addPage();
      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;
    }

    pdf.save(`contract_${contractData?.signature?.id || 'completed'}.pdf`);
  };

  if (loading) {
    return (
      <>
        <PageTitle title="Loading Contract - Blitz" />
        <Container maxWidth="md" sx={{ py: 4 }}>
          <Box display="flex" flexDirection="column" alignItems="center" gap={2}>
            <CircularProgress size={48} />
            <Typography variant="h6">Loading contract...</Typography>
          </Box>
        </Container>
      </>
    );
  }

  if (error) {
    return (
      <>
        <PageTitle title="Contract Error - Blitz" />
        <Container maxWidth="md" sx={{ py: 4 }}>
          <Alert severity="error" sx={{ mb: 3 }}>
            <Typography variant="h6" gutterBottom>Contract Access Error</Typography>
            <Typography>{error}</Typography>
          </Alert>
          <Box display="flex" gap={2}>
            <Button variant="outlined" onClick={handleClose} startIcon={<BackIcon />}>Go Back</Button>
            <Button variant="contained" onClick={load} startIcon={<RefreshIcon />}>Try Again</Button>
          </Box>
        </Container>
      </>
    );
  }

  if (!contractData) {
    return (
      <>
        <PageTitle title="Contract Not Found - Blitz" />
        <Container maxWidth="md" sx={{ py: 4 }}>
          <Alert severity="warning">Contract not found or access denied.</Alert>
          <Box mt={2}>
            <Button variant="outlined" onClick={handleClose} startIcon={<BackIcon />}>
              Go Back
            </Button>
          </Box>
        </Container>
      </>
    );
  }

  const { signature, assignment, template, user, company, processed_contract_body, is_simple, all_signatures } = contractData;
  const showSignatureList = ['company', 'team'].includes(assignment?.assignment_scope);

  // helper for signature list
  const signatureRows = (all_signatures || []).sort((a, b) => {
    // signed/agreed first
    const order = { signed: 0, agreed: 0, pending: 1, declined: 2 };
    return (order[a.status] ?? 99) - (order[b.status] ?? 99);
  });

  // RENDER COMPLETED
  if (completed || (signature && signature.status !== 'pending')) {
    const isAgreed = signature.status === 'agreed';
    const isSigned = signature.status === 'signed';
    const isDeclined = signature.status === 'declined';
    const completedDate = isSigned
      ? signature.signed_at
      : isAgreed
        ? signature.agreed_at
        : signature.declined_at;

    return (
      <>
        <PageTitle title={`Contract ${signature.status} - Blitz`} />
        <Container maxWidth="md" sx={{ py: 4 }}>
          <Box mb={2}>
            <IconButton onClick={handleClose} sx={{ mr: 1 }}>
              <BackIcon />
            </IconButton>
            <Typography variant="h6" component="span">Back to Dashboard</Typography>
          </Box>

          <Card sx={{ mb: 3 }}>
            <CardContent sx={{ textAlign: 'center', py: 4 }}>
              {(isAgreed || isSigned) && (
                <>
                  <SignedIcon color="success" sx={{ fontSize: 64, mb: 2 }} />
                  <Typography variant="h4" color="success.main" gutterBottom>
                    {isAgreed ? 'Agreement Confirmed!' : 'Contract Signed!'}
                  </Typography>
                  <Typography variant="body1" color="text.secondary" gutterBottom>
                    Thank you for {isAgreed ? 'agreeing to' : 'signing'} the contract.
                  </Typography>
                </>
              )}
              {isDeclined && (
                <>
                  <ErrorIcon color="error" sx={{ fontSize: 64, mb: 2 }} />
                  <Typography variant="h4" color="error.main" gutterBottom>
                    Contract Declined
                  </Typography>
                  <Typography variant="body1" color="text.secondary" gutterBottom>
                    You have declined this contract.
                  </Typography>
                  {signature.decline_reason && (
                    <Typography variant="body2" sx={{ mt: 2, fontStyle: 'italic' }}>
                      Reason: {signature.decline_reason}
                    </Typography>
                  )}
                </>
              )}

              <Box sx={{ mt: 3, p: 2, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
                <Typography variant="h6">{template?.name || 'Contract'}</Typography>
                <Typography variant="body2" color="text.secondary">
                  {company?.name || 'Company'}
                </Typography>
                <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                  {completedDate && `Completed on: ${new Date(completedDate).toLocaleString()}`}
                </Typography>
              </Box>

              <Button variant="contained" onClick={handleClose} sx={{ mt: 3, mr: 2 }} startIcon={<BackIcon />}>
                Back to Dashboard
              </Button>
              <Button variant="outlined" sx={{ mt: 3 }} startIcon={<DownloadIcon />} onClick={downloadPdf}>
                Download PDF
              </Button>
            </CardContent>
          </Card>

          {/* --- NEW SIGNATURE LIST (completed view) --- */}
          {showSignatureList && signatureRows.length > 0 && (
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>Signatures</Typography>
                {signatureRows.map(s => (
                  <Box key={s.id} display="flex" alignItems="center" justifyContent="space-between" sx={{ py: 1 }}>
                    <Box>
                      <Typography variant="body2">
                        {s.user
                          ? `${s.user.first_name} ${s.user.last_name} (${s.user.email})`
                          : `User ${s.user_id}`}
                      </Typography>
                      {s.status !== 'pending' && (
                        <Typography variant="caption" color="text.secondary">
                          {s.status === 'signed' && s.signed_at && `Signed: ${new Date(s.signed_at).toLocaleString()}`}
                          {s.status === 'agreed' && s.agreed_at && `Agreed: ${new Date(s.agreed_at).toLocaleString()}`}
                          {s.status === 'declined' && s.declined_at && `Declined: ${new Date(s.declined_at).toLocaleString()}`}
                        </Typography>
                      )}
                    </Box>
                    <Chip label={s.status} color={statusColor(s.status)} size="small" />
                  </Box>
                ))}
              </CardContent>
            </Card>
          )}

          <Card ref={completedDocRef}>
            <CardContent sx={{ position: 'relative', p: 4 }}>
              <Box
                sx={{ fontFamily: 'Arial, sans-serif', lineHeight: 1.6 }}
                dangerouslySetInnerHTML={{ __html: processed_contract_body || template?.contract_body || '' }}
              />
              <Box
                sx={{
                  position: 'absolute',
                  bottom: 24,
                  right: 24,
                  border: '2px solid #4caf50',
                  borderRadius: 1,
                  p: 2,
                  width: 240,
                  bgcolor: 'rgba(255,255,255,0.9)',
                  textAlign: 'left'
                }}
              >
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                  {isDeclined ? 'DECLINED' : isAgreed ? 'AGREED' : 'SIGNED'}
                </Typography>
                <Typography variant="body2">Name: {user ? `${user.first_name} ${user.last_name}` : 'User'}</Typography>
                <Typography variant="body2">Email: {user?.email || '—'}</Typography>
                {completedDate && (
                  <Typography variant="body2">Date: {new Date(completedDate).toLocaleString()}</Typography>
                )}
                {signature.ip_address && (
                  <Typography variant="body2">IP: {signature.ip_address}</Typography>
                )}
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                  Signature ID: {signature.id}
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Container>
      </>
    );
  }

  // PENDING VIEW
  return (
    <>
      <PageTitle title={`${template?.name || 'Contract'} - Blitz`} />
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Box display="flex" alignItems="center" gap={2} mb={3}>
          <IconButton onClick={handleClose}><BackIcon /></IconButton>
          <Typography variant="h6">Contract Details</Typography>
        </Box>

        <Box display="flex" alignItems="center" gap={2} mb={3}>
          <ContractIcon fontSize="large" color="primary" />
          <Box>
            <Typography variant="h4" gutterBottom>{template?.name || 'Contract'}</Typography>
            <Typography variant="subtitle1" color="text.secondary">
              {company?.name || 'Company'}
            </Typography>
            <Chip
              label={is_simple ? 'Agreement' : 'Contract'}
              color={is_simple ? 'info' : 'primary'}
              size="small"
              sx={{ mt: 1 }}
            />
          </Box>
        </Box>

        {user && (
          <Alert severity="info" sx={{ mb: 3 }}>
            <Typography variant="body2">
              <strong>Assigned to:</strong> {user.first_name} {user.last_name} ({user.email})
            </Typography>
          </Alert>
        )}

        {assignment?.assignment_message && (
          <Alert severity="warning" sx={{ mb: 3 }}>
            <Typography variant="body2">
              <strong>Message:</strong> {assignment.assignment_message}
            </Typography>
          </Alert>
        )}

        {assignment?.due_date && (
          <Alert severity="warning" sx={{ mb: 3 }}>
            <Typography variant="body2">
              <strong>Due Date:</strong> {new Date(assignment.due_date).toLocaleString()}
            </Typography>
          </Alert>
        )}

        {/* --- NEW SIGNATURE LIST (pending view) --- */}
        {showSignatureList && signatureRows.length > 0 && (
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Organization Signatures
              </Typography>
              <Typography variant="caption" color="text.secondary" gutterBottom display="block">
                Track who has already completed this {is_simple ? 'agreement' : 'contract'}.
              </Typography>
              <Divider sx={{ my: 1 }} />
              {signatureRows.map(s => (
                <Box key={s.id} display="flex" alignItems="center" justifyContent="space-between" sx={{ py: 1 }}>
                  <Box>
                    <Typography variant="body2">
                      {s.user
                        ? `${s.user.first_name} ${s.user.last_name} (${s.user.email})`
                        : `User ${s.user_id}`}
                      {s.id === signature.id && (
                        <Tooltip title="You">
                          <Chip size="small" label="You" sx={{ ml: 1 }} />
                        </Tooltip>
                      )}
                    </Typography>
                    {s.status !== 'pending' && (
                      <Typography variant="caption" color="text.secondary">
                        {s.status === 'signed' && s.signed_at && `Signed: ${new Date(s.signed_at).toLocaleString()}`}
                        {s.status === 'agreed' && s.agreed_at && `Agreed: ${new Date(s.agreed_at).toLocaleString()}`}
                        {s.status === 'declined' && s.declined_at && `Declined: ${new Date(s.declined_at).toLocaleString()}`}
                      </Typography>
                    )}
                  </Box>
                  <Chip label={s.status} color={statusColor(s.status)} size="small" />
                </Box>
              ))}
            </CardContent>
          </Card>
        )}

        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box
              sx={{
                maxHeight: '500px',
                overflow: 'auto',
                border: '1px solid #e0e0e0',
                borderRadius: 1,
                p: 3,
                backgroundColor: '#fafafa'
              }}
            >
              <div
                dangerouslySetInnerHTML={{
                  __html: processed_contract_body || template?.contract_body || 'Loading contract content...'
                }}
                style={{ fontFamily: 'Arial, sans-serif', lineHeight: '1.6' }}
              />
            </Box>
          </CardContent>
        </Card>

        <Alert severity="info" sx={{ mb: 3 }}>
          <Typography variant="body2">
            {is_simple
              ? 'By clicking "I Agree", you confirm that you have read and agree to be bound by the terms above.'
              : 'By clicking "Sign Contract", you agree to be legally bound by the terms and conditions outlined above. Your digital signature will be recorded with timestamp and IP address for legal verification.'}
          </Typography>
        </Alert>

        <Card>
          <CardActions sx={{ p: 3, justifyContent: 'space-between' }}>
            <Button
              variant="outlined"
              color="error"
              onClick={() => setShowDeclineDialog(true)}
              disabled={actionLoading}
            >
              Decline
            </Button>

            <Button
              variant="contained"
              size="large"
              onClick={handleAgree}
              disabled={actionLoading}
              startIcon={actionLoading ? <CircularProgress size={16} /> : <DocumentIcon />}
              sx={{ minWidth: 200 }}
            >
              {actionLoading ? (is_simple ? 'Confirming...' : 'Signing...') : (is_simple ? 'I Agree' : 'Sign Contract')}
            </Button>
          </CardActions>
        </Card>

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>
        )}

        <Dialog open={showDeclineDialog} onClose={() => setShowDeclineDialog(false)} maxWidth="sm" fullWidth>
          <DialogTitle>
            <Box display="flex" justifyContent="space-between" alignItems="center">
              Decline Contract
              <IconButton onClick={() => setShowDeclineDialog(false)}><CloseIcon /></IconButton>
            </Box>
          </DialogTitle>
          <DialogContent>
            <Typography variant="body2" gutterBottom sx={{ mb: 2 }}>
              Please provide a reason for declining this {is_simple ? 'agreement' : 'contract'}:
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={4}
              placeholder="Enter your reason for declining..."
              value={declineReason}
              onChange={(e) => setDeclineReason(e.target.value)}
              variant="outlined"
            />
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button onClick={() => setShowDeclineDialog(false)} disabled={actionLoading}>Cancel</Button>
            <Button
              variant="contained"
              color="error"
              onClick={handleDecline}
              disabled={!declineReason.trim() || actionLoading}
              startIcon={actionLoading ? <CircularProgress size={16} /> : null}
            >
              {actionLoading ? 'Declining...' : 'Confirm Decline'}
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </>
  );
};

export default DirectContractAccess;
